import { useState } from 'react';
import type { WorkOrder, WorkOrderEvent, WorkOrderStatus } from '@shared/domain';
import { WORKORDER_TRANSITIONS } from '@shared/rules';
import { useAuth } from '@/state/auth';
import { useCreateWorkOrder, useEquipment, useSetWorkOrderStatus, useUpdateWorkOrder, useWorkOrder, useWorkOrders } from '@/lib/query';
import { ts } from '@/lib/format';
import { Badge, Card, DataTable, EmptyHint, SectionTitle } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

const STATUS_LABEL: Record<string, string> = { open: '待处理', assigned: '已指派', in_progress: '处理中', resolved: '已解决', closed: '已关闭' };
const STATUS_STYLE: Record<string, string> = {
  open: 'bg-slate-100 text-slate-600 border-slate-200',
  assigned: 'bg-info-bg text-info-fg border-info-border',
  in_progress: 'bg-warn-bg text-warn-fg border-warn-border',
  resolved: 'bg-ok-bg text-ok-fg border-ok-border',
  closed: 'bg-slate-100 text-slate-400 border-slate-200',
};
const NEXT_LABEL: Record<string, string> = { assigned: '指派', in_progress: '开始', resolved: '解决', closed: '关闭' };

function EventTrail({ id }: { id: string }) {
  const { data } = useWorkOrder(id);
  return (
    <div className="space-y-1 rounded-lg bg-slate-50 p-3">
      {(data?.events ?? []).map((e: WorkOrderEvent) => (
        <div key={e.id} className="flex justify-between text-[12px] text-slate-500">
          <span>{e.action}{e.fromStatus ? ` (${STATUS_LABEL[e.fromStatus]}→${STATUS_LABEL[e.toStatus ?? '']})` : ''}{e.note ? ` · ${e.note}` : ''}</span>
          <span className="tabular-nums">{ts(e.at, false)} · {e.actor}</span>
        </div>
      ))}
    </div>
  );
}

export default function WorkOrders() {
  const { user } = useAuth();
  const canManage = ['supervisor', 'engineer', 'admin'].includes(user?.role ?? '');
  const [filter, setFilter] = useState<WorkOrderStatus | undefined>(undefined);
  const { data: orders } = useWorkOrders(filter);
  const { data: equip } = useEquipment();
  const create = useCreateWorkOrder();
  const update = useUpdateWorkOrder();
  const setStatus = useSetWorkOrderStatus();
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', priority: 'medium', assignee: '', equipmentSlug: '' });
  const [msg, setMsg] = useState<string | null>(null);

  const submit = () => {
    if (!form.title.trim()) return;
    create.mutate({ title: form.title.trim(), priority: form.priority, assignee: form.assignee || undefined, equipmentSlug: form.equipmentSlug || undefined });
    setForm({ title: '', priority: 'medium', assignee: '', equipmentSlug: '' });
  };
  const reassign = (w: WorkOrder) => {
    const who = window.prompt(`改派负责人（当前：${w.assignee ?? '无'}）`, w.assignee ?? '');
    if (who !== null) { update.mutate({ id: w.id, patch: { assignee: who } }); setMsg(`工单 ${w.id} 已改派`); }
  };
  const advance = (w: WorkOrder, to: WorkOrderStatus) => {
    const note = to === 'resolved' ? window.prompt('解决说明（可留空）') ?? undefined : undefined;
    setStatus.mutate({ id: w.id, status: to, note });
    setMsg(`工单 ${w.id} → ${STATUS_LABEL[to]}`);
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">工单看板</h2>
        <p className="mt-1 text-[14px] text-slate-500">告警/手动派单 → 指派 → 处理 → 解决 → 关闭，全程留痕。</p>
      </div>

      {msg ? <div className="mb-4 rounded-lg bg-info-bg px-4 py-2 text-[13px] text-info-fg">{msg}</div> : null}

      <div className="mb-4 flex flex-wrap gap-2">
        {(['', 'open', 'assigned', 'in_progress', 'resolved', 'closed'] as const).map((s) => (
          <button key={s || 'all'} onClick={() => setFilter((s || undefined) as WorkOrderStatus | undefined)} className={`btn ${filter === (s || undefined) ? 'btn-dark' : 'btn-ghost'}`}>
            {s ? STATUS_LABEL[s] : '全部'}
          </button>
        ))}
      </div>

      <Card className="mb-6 p-0">
        <DataTable
          columns={[
            { key: 'id', header: '工单', render: (w: WorkOrder) => <span className="font-mono text-[13px]">{w.id}</span> },
            { key: 'title', header: '标题', render: (w: WorkOrder) => <span>{w.title}{w.source === 'alarm' ? <Badge className="ml-2 bg-alarm-bg text-alarm-fg border-alarm-border">告警</Badge> : null}</span> },
            { key: 'priority', header: '优先级', render: (w: WorkOrder) => <span className={w.priority === 'high' ? 'text-alarm-fg' : 'text-slate-600'}>{w.priority}</span> },
            { key: 'assignee', header: '负责人', render: (w: WorkOrder) => <span className="text-slate-500">{w.assignee ?? '—'}</span> },
            { key: 'equipment', header: '关联设备', render: (w: WorkOrder) => <span className="font-mono text-[13px] text-slate-500">{w.equipmentSlug ?? '—'}</span> },
            { key: 'status', header: '状态', render: (w: WorkOrder) => <span className={`pill ${STATUS_STYLE[w.status]}`}>{STATUS_LABEL[w.status]}</span> },
            { key: 'createdAt', header: '创建', render: (w: WorkOrder) => <span className="tabular-nums text-slate-500">{ts(w.createdAt, false)}</span> },
            {
              key: 'ops',
              header: '操作',
              render: (w: WorkOrder) => (
                <div className="flex flex-wrap items-center gap-2 text-[13px]">
                  {canManage && (WORKORDER_TRANSITIONS[w.status] ?? []).map((to) => (
                    <button key={to} onClick={() => advance(w, to as WorkOrderStatus)} className="text-brand-600 hover:underline">{NEXT_LABEL[to] ?? to}</button>
                  ))}
                  {canManage && w.status !== 'closed' && <button onClick={() => reassign(w)} className="text-slate-500 hover:underline">改派</button>}
                  <button onClick={() => setOpen(open === w.id ? null : w.id)} className="text-slate-500 hover:underline">{open === w.id ? '收起' : '详情'}</button>
                </div>
              ),
            },
          ]}
          rows={orders ?? []}
          empty={<EmptyHint>暂无工单</EmptyHint>}
        />
      </Card>

      {open ? <Card className="mb-6 p-5"><SectionTitle title={`处置留痕 · ${open}`} /><EventTrail id={open} /></Card> : null}

      {canManage ? (
        <Card className="p-6">
          <SectionTitle title="新建工单" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <label className="text-[13px] text-slate-500 md:col-span-2">标题
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            </label>
            <label className="text-[13px] text-slate-500">优先级
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm">
                <option value="low">low</option><option value="medium">medium</option><option value="high">high</option>
              </select>
            </label>
            <label className="text-[13px] text-slate-500">负责人(可空)
              <input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            </label>
            <label className="text-[13px] text-slate-500">关联设备
              <select value={form.equipmentSlug} onChange={(e) => setForm({ ...form, equipmentSlug: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm">
                <option value="">（无）</option>
                {(equip?.items ?? []).map((eq) => (<option key={eq.slug} value={eq.slug}>{eq.name}</option>))}
              </select>
            </label>
          </div>
          <button onClick={submit} disabled={create.isPending || !form.title.trim()} className="btn btn-primary mt-4 disabled:opacity-60">{create.isPending ? '创建中…' : '创建工单'}</button>
        </Card>
      ) : null}
    </div>
  );
}
